<template>
  <div class="blog-card">
    <a :href="getPostUrl()" class="card-link">
      <div class="card bg-dark text-white h-100">
        <!-- Blog image -->
        <div class="card-img-wrapper">
          <div class="image-container">
            <img 
              :src="getCardImage()"
              :alt="blog.title" 
              class="card-img"
            >
            <div class="image-overlay">
              <div class="overlay-content">
                <FaIcon name="arrow-right" />
                <span class="overlay-text">Leer más</span>
              </div>
            </div>
          </div>
        </div>

        <!-- Blog info -->
        <div class="card-body d-flex flex-column">
          <div class="blog-info">
            <div class="d-flex align-items-center gap-2 mb-2">
              <span 
                v-if="blog.category"
                class="category-tag"
                :style="{ borderColor: blog.category.color, color: blog.category.color }"
              >
                {{ blog.category.name }}
              </span>
              <span class="date-tag">{{ formatDate(blog.published_at || blog.created_at) }}</span>
            </div>
            <h3 class="blog-title text-white mt-2 mb-3">{{ blog.title }}</h3>
            <p class="blog-excerpt text-white-50">{{ getCardExcerpt() }}</p>
            
            <!-- Post meta -->
            <div class="post-meta d-flex align-items-center gap-3 mt-auto mb-3">
              <small class="text-muted">
                <FaIcon name="user" class="me-1" />
                {{ blog.author?.first_name || blog.author?.username }}
              </small>
              <small class="text-muted">
                <FaIcon name="clock" class="me-1" />
                {{ blog.reading_time }} min
              </small>
              <small class="text-muted">
                <FaIcon name="eye" class="me-1" />
                {{ blog.views_count }}
              </small>
            </div>
          </div>
          <div class="btn btn-primary">
            <FaIcon name="book" class="me-2" />
            Leer más
          </div>
        </div>
      </div>
    </a>
  </div>
</template>

<script setup>
import { defineProps } from 'vue';
import { cleanCloudinaryUrl } from '@/utils/text';

const props = defineProps({
  blog: {
    type: Object,
    required: true,
  }
});

const getCardImage = () => {
  if (props.blog.featured_image) return cleanCloudinaryUrl(props.blog.featured_image);
  if (props.blog.sections && props.blog.sections.length > 0 && props.blog.sections[0].image) {
    return cleanCloudinaryUrl(props.blog.sections[0].image);
  }
  return require('@/assets/images/jamrock-logo.webp');
};

const getCardExcerpt = () => {
  if (props.blog.excerpt) return props.blog.excerpt;
  if (props.blog.sections && props.blog.sections.length > 0 && props.blog.sections[0].text) {
    // Limitar a 180 caracteres
    return props.blog.sections[0].text.slice(0, 180) + (props.blog.sections[0].text.length > 180 ? '...' : '');
  }
  return '';
};

const formatDate = (dateString) => {
  if (!dateString) return '';
  return new Date(dateString).toLocaleDateString('es-ES', {
    year: 'numeric',
    month: 'short',
    day: 'numeric'
  });
};

const getPostUrl = () => {
  if (props.blog) {
    if (props.blog.slug) {
      return `/blog/${props.blog.slug}`;
    } else if (props.blog.id) {
      return `/blog/${props.blog.id}`;
    }
  }
  return '#';
};
</script>

<style scoped>
.blog-card {
  height: 100%;
  transition: all 0.3s ease;
  border-radius: 12px;
  overflow: hidden;
}

.card-link {
  text-decoration: none;
  color: inherit;
  display: block;
  height: 100%;
}

.card-link:hover {
  text-decoration: none;
  color: inherit;
}

.card {
  border-bottom: 1px solid var(--primary-color);
  border-radius: 12px;
  background: linear-gradient(145deg, var(--black-color-2), var(--black-color-3));
  overflow: hidden;
  transition: all 0.3s ease;
  height: 100%;
  display: flex;
  flex-direction: column;
}

.card-link:hover .card {
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.3);
  border-color: var(--primary-color);
}

.card-img-wrapper {
  width: 100%;
  display: block;
  position: relative;
}

.image-container {
  aspect-ratio: 16/9;
  overflow: hidden;
  position: relative;
  border-radius: 12px 12px 0 0;
}

.card-img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: transform 0.5s ease;
  filter: brightness(.5)
}

.image-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.3);
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0;
  transition: opacity 0.3s ease;
  backdrop-filter: blur(2px);
}

.overlay-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.5rem;
  color: white;
  text-align: center;
}

.image-overlay i {
  color: white;
  font-size: 1.5rem;
  transform: translateX(-10px);
  transition: transform 0.3s ease;
}

.overlay-text {
  font-size: 0.9rem;
  font-weight: 500;
  opacity: 0;
  transform: translateY(10px);
  transition: all 0.3s ease;
}

.card-link:hover .card-img {
  transform: scale(1.05);
  filter: brightness(1);
}

.card-link:hover .image-overlay {
  opacity: 1;
}

.card-link:hover .image-overlay i {
  transform: translateX(0);
}

.card-link:hover .overlay-text {
  opacity: 1;
  transform: translateY(0);
}

.category-tag, .date-tag {
  display: inline-block;
  padding: 0.25rem 0.5rem;
  border: 1px solid var(--primary-color);
  background-color: transparent;
  color: var(--white-color-2);
  font-size: 0.75rem;
  border-radius: 4px;
  font-weight: 500;
  transition: all 0.3s ease;
}

.date-tag {
  background: var(--dark-primary-color-2);
  border-color: var(--border-color);
}

.category-tag:hover {
  transform: scale(1.05);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
}

.blog-title {
  font-size: 1.25rem;
  margin: 0.5rem 0;
  line-height: 1.3;
  font-weight: 500;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  transition: color 0.3s ease;
}

.blog-excerpt {
  font-size: 0.9rem;
  line-height: 1.5;
  margin-bottom: 1rem;
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
  color: var(--text-muted);
  transition: color 0.3s ease;
}

.post-meta {
  font-size: 0.8rem;
  flex-wrap: wrap;
  gap: 0.5rem;
}

.post-meta i {
  font-size: 0.7rem;
}

.btn {
  margin-top: auto;
}

/* Responsive adjustments */
@media (max-width: 768px) {
  .blog-card {
    margin-bottom: 1rem;
  }
  
  .blog-title {
    font-size: 1.1rem;
    margin-bottom: 0.75rem;
  }
  
  .blog-excerpt {
    font-size: 0.85rem;
    margin-bottom: 0.75rem;
  }
  
  .btn {
    padding: 0.6rem 1.2rem;
    font-size: 0.85rem;
  }
  
  .overlay-content {
    gap: 0.25rem;
  }
  
  .overlay-text {
    font-size: 0.8rem;
  }
  
  .image-overlay i {
    font-size: 1.25rem;
  }
}

@media (max-width: 576px) {
  .blog-card {
    margin-bottom: 0.75rem;
  }
  
  .card {
    border-radius: 8px;
  }
  
  .image-container {
    border-radius: 8px 8px 0 0;
  }
  
  .blog-title {
    font-size: 1rem;
    margin-bottom: 0.5rem;
  }
  
  .blog-excerpt {
    font-size: 0.8rem;
    margin-bottom: 0.5rem;
    -webkit-line-clamp: 2;
  }
  
  .category-tag, .date-tag {
    font-size: 0.7rem;
    padding: 0.2rem 0.4rem;
  }
  
  .post-meta {
    font-size: 0.7rem;
    gap: 0.2rem;
  }
  
  .btn {
    padding: 0.5rem 1rem;
    font-size: 0.8rem;
    border-radius: 20px;
  }
  
  .overlay-text {
    font-size: 0.75rem;
  }
  
  .image-overlay i {
    font-size: 1.1rem;
  }
}

@media (max-width: 480px) {
  .blog-card {
    margin-bottom: 0.5rem;
  }
  
  .card-body {
    padding: 1rem;
  }
  
  .blog-title {
    font-size: 0.95rem;
  }
  
  .blog-excerpt {
    font-size: 0.75rem;
  }
  
  .post-meta {
    font-size: 0.65rem;
  }
  
  .btn {
    padding: 0.4rem 0.8rem;
    font-size: 0.75rem;
  }
}

/* Touch device optimizations */
@media (hover: none) and (pointer: coarse) {
  .card-link:hover .card {
    transform: none;
  }
  
  .card-link:active .card {
    transform: scale(0.98);
  }
  
  .btn:hover {
    transform: none;
  }
  
  .btn:active {
    transform: scale(0.95);
  }
}
</style>
